import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProviderByUid } from '@/services/providers';
import { captureException } from '../lib/monitoring';
import type { Provider } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute
 * - Redirects unauthenticated users to /signin
 * - Redirects pending providers to /awaiting-approval
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [checking, setChecking] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchProvider = useCallback(async () => {
    // When we start a fetch, mark as checking and clear prior errors
    setChecking(true);
    setFetchError(null);

    if (!currentUser) {
      // No user, nothing to fetch
      setProvider(null);
      setChecking(false);
      return;
    }

    try {
      const p = await getProviderByUid(currentUser.uid);
      setProvider(p);
    } catch (err) {
      captureException(err, { context: 'ProtectedRoute - getProviderByUid' });
      setFetchError("We couldn't load your account details. Please try again.");
    } finally {
      setChecking(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchProvider();
  }, [fetchProvider]);

  const handleRetry = () => {
    fetchProvider();
  };

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Surface provider fetch errors with a retry action, instead of rendering children incorrectly
  if (fetchError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white shadow-sm border border-gray-200 rounded-xl p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-600 mb-4">{fetchError}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ✅ Redirect users with pending approval status
  if (provider?.status === 'pending') {
    return <Navigate to="/awaiting-approval" replace />;
  }

  // Otherwise, allow access
  return <>{children}</>;
};

export default ProtectedRoute;
